import { Box, Typography, IconButton } from '@mui/material';
import { styled, SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { Z_LAYERS } from 'app/pages/mobile/utils/constants';

const TRANSITION_MS = 300;

const StyledBottomSheet = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: theme.spacing(2),
  height: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: Z_LAYERS.MOBILE_BOTTOM_SHEET,
  boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
  transform: 'translateY(100%)',
  transition: 'transform 0.3s ease-in-out',
  '&.open': {
    transform: 'translateY(0)',
  },
}));

const StyledHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  flexShrink: 0,
});

const StyledContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
});

const StyledBackdrop = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: Z_LAYERS.MOBILE_BOTTOM_SHEET_BACKDROP,
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
  '&.open': {
    opacity: 1,
    visibility: 'visible',
  },
});

interface MobileBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  // Opt-in visual affordances (default off to preserve existing call sites):
  // a centered grab handle at the top of the sheet, and hiding the close (X).
  showDragHandle?: boolean;
  hideCloseButton?: boolean;
}

const DragHandle = styled(Box)(({ theme }) => ({
  width: 36,
  height: 4,
  borderRadius: 2,
  backgroundColor: theme.palette.grey[300],
  alignSelf: 'center',
  marginBottom: theme.spacing(1.5),
  flexShrink: 0,
}));

const MobileBottomSheet = ({
  open,
  onClose,
  title,
  children,
  sx,
  showDragHandle = false,
  hideCloseButton = false,
}: MobileBottomSheetProps) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const animationFrameId = useRef(0);
  const unmountTimerId = useRef<ReturnType<typeof setTimeout>>();

  // When open becomes true, mount the portal (and cancel any pending unmount)
  useEffect(() => {
    if (open) {
      clearTimeout(unmountTimerId.current);
      setMounted(true);
    }
  }, [open]);

  // Once mounted, animate in; when open becomes false, animate out then unmount
  useEffect(() => {
    if (open && mounted) {
      animationFrameId.current = requestAnimationFrame(() => {
        animationFrameId.current = requestAnimationFrame(() => setVisible(true));
      });
    } else if (!open && mounted) {
      cancelAnimationFrame(animationFrameId.current);
      setVisible(false);
      unmountTimerId.current = setTimeout(() => setMounted(false), TRANSITION_MS);
    }
    return () => cancelAnimationFrame(animationFrameId.current);
  }, [open, mounted]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationFrameId.current);
      clearTimeout(unmountTimerId.current);
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <>
      <StyledBackdrop className={visible ? 'open' : ''} onClick={onClose} />
      <StyledBottomSheet className={visible ? 'open' : ''} sx={sx}>
        {showDragHandle && <DragHandle data-testid="mobile-bottom-sheet-drag-handle" />}
        <StyledHeader>
          <Typography variant="h6">{title}</Typography>
          {!hideCloseButton && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </StyledHeader>
        <StyledContent>{children}</StyledContent>
      </StyledBottomSheet>
    </>,
    document.body
  );
};

export default MobileBottomSheet;
